<template>
  <span class="feature-tag" :style="{'--bg-color': tagData.backgroundColor, '--font-color': tagData.fontColor, '--border-color': tagData.borderColor }">
    <slot>
      <span class="detail-md-bold d-flex align-center">
        <template v-if="tagData.icon">
          <app-material-icon
            :name="tagData.icon"
            :size="size === 'sm' ? 'xxs' : 'xs'"
            :color="tagData.iconColor"
            class="mr-1"
          />
        </template>
        <span>{{ $t(`common.AppFeatureTag.textKeys.${tagData.textKey}`) }}</span>
      </span>
    </slot>
  </span>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { useTheme } from 'vuetify'

export type FeatureTag = 'beta' | 'new' | 'upcoming' | 'upgrade'
type FeatureTagSize = 'sm' | 'md'

type TagData = {
  backgroundColor: string;
  fontColor: string;
  borderColor: string;
  icon?: string;
  iconColor?: string;
  textKey: string;
}

export default defineComponent({
  name: 'AppFeatureTag',
  setup () {
    const { current: theme } = useTheme()

    return {
      betaColor: {
        background: theme.value.colors['surface-lime-subtle'],
        border: theme.value.colors['border-lime'],
        font: theme.value.colors['text-lime'],
      },
      newColor: {
        background: theme.value.colors['surface-blue-subtle'],
        border: theme.value.colors['border-blue'],
        font: theme.value.colors['text-blue'],
      },
      upcomingColor: {
        background: theme.value.colors['surface-grey-subtle'],
        border: theme.value.colors['border-grey'],
        font: theme.value.colors['text-grey'],
      },
      upgradeColor: {
        background: theme.value.colors['surface-orange-subtle'],
        border: theme.value.colors['border-orange'],
        font: theme.value.colors['text-orange'],
      },
    }
  },
  props: {
    size: {
      type: String as PropType<FeatureTagSize>,
      default: 'md',
    },
    tag: {
      type: String as PropType<FeatureTag>,
      required: true,
    },
  },
  computed: {
    tagData (): TagData {
      const data = {
        beta: {
          backgroundColor: this.betaColor.background,
          fontColor: this.betaColor.font,
          borderColor: this.betaColor.border,
          textKey: 'beta',
        },
        new: {
          backgroundColor: this.newColor.background,
          fontColor: this.newColor.font,
          borderColor: this.newColor.border,
          textKey: 'new',
        },
        upcoming: {
          backgroundColor: this.upcomingColor.background,
          fontColor: this.upcomingColor.font,
          borderColor: this.upcomingColor.border,
          textKey: 'upcoming',
        },
        upgrade: {
          backgroundColor: this.upgradeColor.background,
          fontColor: this.upgradeColor.font,
          borderColor: this.upgradeColor.border,
          icon: 'crown',
          iconColor: 'icon-orange',
          textKey: 'upgrade',
        },
      }

      return data[this.tag]
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.feature-tag {
  background: var(--bg-color);
  color: var(--font-color);
  border: get-border(var(--border-color));
  text-transform: none;

  user-select: none;
  @include border-radius('border-radius-pill');
  padding: 2px 12px;
}
</style>
